// Nested-folder support is layered on top of the existing flat Tag model
// WITHOUT any backend change: hierarchy is encoded in the tag `label` using a
// `/` separator (e.g. `acme`, `acme/2024`, `acme/2024/final`). Existing labels
// are sanitised to [a-z0-9\-_], so none can contain `/` — every legacy folder
// therefore parses as a top-level (root) node with zero migration.

export const SEPARATOR = '/';

export const splitLabel = (label) => (label || '').split(SEPARATOR);

// Parent path of a label, or null for a root label.
export const parentLabel = (label) => {
  const idx = (label || '').lastIndexOf(SEPARATOR);
  return idx === -1 ? null : label.slice(0, idx);
};

// Last path segment — the piece shown in the UI for a nested folder.
export const lastSegment = (label) => {
  const idx = (label || '').lastIndexOf(SEPARATOR);
  return idx === -1 ? label || '' : label.slice(idx + 1);
};

// Sanitise a SINGLE segment with the exact rule the flat sidebar already used.
// Never run this over a full path — it would turn the `/` separators into `-`
// and destroy the hierarchy.
export const sanitizeSegment = (raw) =>
(raw || '').trim().toLowerCase().replace(/[^a-z0-9\-_]/g, '-');

// Build a child's full-path label from a parent path + a raw new segment.
// Returns null when the segment sanitises to empty.
export const buildChildLabel = (parent, rawSegment) => {
  const seg = sanitizeSegment(rawSegment);
  if (!seg) {return null;}
  return parent ? `${parent}${SEPARATOR}${seg}` : seg;
};

// Ancestor prefixes of a label, excluding the label itself. `acme/2024/final`
// -> ['acme', 'acme/2024']. Used to auto-expand a branch down to a selection.
export const ancestorPaths = (label) => {
  const parts = splitLabel(label);
  const paths = [];
  let prefix = '';
  for (let i = 0; i < parts.length - 1; i += 1) {
    prefix = i === 0 ? parts[0] : `${prefix}${SEPARATOR}${parts[i]}`;
    paths.push(prefix);
  }
  return paths;
};

// A folder and everything nested beneath it (self + descendants), as tag
// objects. Single source of truth for the subtree predicate used by nested
// filtering, cascade rename and subtree delete.
export const getDescendantTags = (allTags = [], label) => {
  const prefix = `${label}${SEPARATOR}`;
  return allTags.filter((t) => t.label === label || t.label.startsWith(prefix));
};

// tagIds of a folder and everything nested beneath it — used to expand a parent
// selection into the descendant tagIds the asset query filters on.
export const getDescendantTagIds = (allTags = [], label) =>
getDescendantTags(allTags, label).map((t) => t.tagId);

// Return a NEW Set with `key` toggled (added if absent, removed if present).
export const toggleInSet = (set, key) => {
  const next = new Set(set);
  if (next.has(key)) {next.delete(key);} else {next.add(key);}
  return next;
};

// Build a tree from the flat tag list.
// Returns { roots: Node[], byPath: Map<path, Node> }.
// Node: { path, segment, depth, tag, tagId, directCount, children, isPlaceholder }.
// Intermediate prefixes that have no real tag row become placeholder nodes so a
// deep label never makes an ancestor branch disappear.
export const buildTagTree = (allTags = []) => {
  const byPath = new Map();

  const ensureNode = (path) => {
    if (byPath.has(path)) {return byPath.get(path);}
    const node = {
      path,
      segment: lastSegment(path),
      depth: splitLabel(path).length - 1,
      tag: null,
      tagId: null,
      directCount: 0,
      children: [],
      isPlaceholder: true
    };
    byPath.set(path, node);
    const parent = parentLabel(path);
    if (parent !== null) {
      ensureNode(parent).children.push(node);
    }
    return node;
  };

  allTags.forEach((tag) => {
    const parts = splitLabel(tag.label);
    let prefix = '';
    for (let i = 0; i < parts.length; i += 1) {
      prefix = i === 0 ? parts[0] : `${prefix}${SEPARATOR}${parts[i]}`;
      ensureNode(prefix);
    }
    const node = byPath.get(tag.label);
    node.tag = tag;
    node.tagId = tag.tagId;
    node.directCount = tag.count || 0;
    node.isPlaceholder = false;
  });

  const roots = [];
  byPath.forEach((node) => {
    if (parentLabel(node.path) === null) {roots.push(node);}
  });

  // Sort every level alphabetically by segment (mirrors the old localeCompare).
  const sortNodes = (nodes) => {
    nodes.sort((a, b) => a.segment.localeCompare(b.segment));
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return { roots, byPath };
};

// Map<path, totalCount> — each node's own count plus every descendant's, with
// the optimistic drag-drop deltas folded in so a drop into a child immediately
// bumps its ancestors.
//
// NOTE: the server returns an independent `count` per tag, so an asset tagged
// into BOTH a folder and one of its subfolders is counted once for each tag and
// the total can exceed the number of distinct assets. Per-tag counts are the
// only data the client has; a deduplicated subtree count needs a backend field.
export const buildSubtreeCounts = (roots = [], adjustments = {}) => {
  const totals = new Map();
  const visit = (node) => {
    let sum = node.directCount + (adjustments[node.path] || 0);
    node.children.forEach((child) => {sum += visit(child);});
    totals.set(node.path, sum);
    return sum;
  };
  roots.forEach(visit);
  return totals;
};

// Paths to keep visible for a search: every full-path match plus its ancestors
// so the matching branch stays reachable/expanded.
export const matchNodesForSearch = (allTags = [], query) => {
  const q = (query || '').toLowerCase();
  const visible = new Set();
  allTags.forEach((t) => {
    if (!t.label.toLowerCase().includes(q)) {return;}
    const parts = splitLabel(t.label);
    let prefix = '';
    for (let i = 0; i < parts.length; i += 1) {
      prefix = i === 0 ? parts[0] : `${prefix}${SEPARATOR}${parts[i]}`;
      visible.add(prefix);
    }
  });
  return visible;
};
